import { ReactNode, useCallback, useEffect, useState } from 'react'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { Modifier, usePopper } from 'react-popper'
import { twMerge } from 'tailwind-merge'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import SearchIcon from 'shared/icons/search-icon'
import { useCommunitySearchSuggestions } from 'modules/community/api/get-community-search-suggestions'
import { buildSearchUrl } from './utils'

interface FormData {
  search: string
}

interface Props {
  className?: string
  placeholder?: string
}

const sameWidth: Modifier<'sameWidth', object> = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`
  },
}

export function CommunitySearchForm({ className, placeholder }: Props): ReactNode {
  const router = useRouter()
  const { asPath, query } = router
  const { communityPath, topicPath, q } = query
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: { search: '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 15] },
      },
      sameWidth,
    ],
  })

  const [debouncedSearch] = useDebouncedValue(watch('search'), 300)
  const { data: suggestions } = useCommunitySearchSuggestions({
    path: communityPath,
    topicPath: topicPath,
    query: isValid ? debouncedSearch : undefined,
  })

  const error = errors.search?.message as ParseKeys
  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen])
  const formRef = useClickOutside<HTMLFormElement>(handleClickOutside)

  useEffect(() => {
    if (asPath.includes('/search') && q) {
      setValue('search', q as string, { shouldValidate: true, shouldDirty: true })
    }
  }, [asPath, q, setValue])

  useEffect(() => {
    setActiveIndex(-1)
  }, [suggestions, isOpen, debouncedSearch])

  const onSubmit = ({ search }: FormData): void => {
    router.push(buildSearchUrl(search, communityPath, topicPath))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!suggestions?.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex(prev => (prev + 1) % suggestions.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1))
      return
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      const selected = suggestions[activeIndex]
      setValue('search', selected)
      router.push(buildSearchUrl(selected, communityPath, topicPath))
      setIsOpen(false)
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={e => {
        void handleSubmit(onSubmit)(e)
      }}
      data-testid="community-search-form"
      className={twMerge(
        'flex flex-col gap-3 rounded-md bg-background-secondary p-5 lg:px-0',
        className,
      )}
    >
      <div className="relative lg:px-5" ref={setReferenceElement}>
        <input
          onFocus={() => {
            setIsOpen(true)
          }}
          onKeyDown={handleKeyDown}
          type="search"
          data-testid="search-input"
          placeholder={placeholder ?? t('global.search')}
          className="max-h-12 w-full rounded-md border border-text-secondary/30 p-4 pr-9 text-text-primary hide-webkit-search-cancel-button focus:border-brand-primary focus:outline-hidden lg:max-h-10"
          {...register('search', {
            validate: value => {
              const trimmed = value.trim()
              if (!trimmed) return false
              if (trimmed.length < 3) return 'community.search.errors.min_length'
              return true
            },
          })}
        />
        <button
          type="submit"
          disabled={!isValid}
          data-testid="search-submit-button"
          title={t('global.search')}
          className="absolute top-1/2 right-7 -translate-y-1/2"
        >
          <SearchIcon className="size-5 text-text-primary" />
        </button>
      </div>
      <p className="text-center text-sm text-danger">{t(error ?? '')}</p>
      {isOpen && suggestions && suggestions.length > 0 && (
        <div
          ref={setPopperElement}
          className="z-30"
          style={{ ...styles.popper }}
          {...attributes.popper}
        >
          <div className="min-w-60 rounded-lg bg-background-secondary py-5 shadow-menu">
            <ul className="flex flex-col gap-2">
              {suggestions.map((suggestion, index) => (
                <li key={suggestion} className="w-full">
                  <Link
                    title={suggestion}
                    href={buildSearchUrl(suggestion, communityPath, topicPath)}
                    className={twMerge(
                      'inline-block w-full cursor-pointer truncate pl-5 text-text-primary hover:bg-background-primary',
                      activeIndex === index && 'bg-background-primary',
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {suggestion}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </form>
  )
}
